// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import type { ColumnDef } from '@usealloy/component-library';
import {
  Banner,
  Box,
  Button,
  ButtonVariant,
  Modal,
  Table,
  useSnackBar,
  Text,
  Badge,
} from '@usealloy/component-library';
import pluralize from 'pluralize';
import cloneDeep from 'lodash/cloneDeep';
import uniqWith from 'lodash/uniqWith';

import { useQueryClient } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import { createMissingOutcomes, createOtherMissingItems, remapThresholdReasonCodeGroupIds } from './helpers';
import type { MissingRequiredItemInfo, PendingImportData } from '../types';
import { WorkflowJSONType } from '../types';
import { importPartialWorkflowData, importWorkflowData } from '../../../../store/redux/slice';
import { mergePendingRules } from '../../../../store/engine/importer/merge';
import { selectWorkflowData, selectWorkflowProps } from '../../../../store/redux/selectors';
import { useWorkflowProps } from '~/dashboard/views/workflows/api';
import { checkExistenceOfDataPassingActions } from '../helpers';


type ReviewWorkflowImportModalProps = {
  closeModal: () => void;
  onSuccessfulImport: (type: WorkflowJSONType, isContainingDataPassingNodes: boolean) => void;
  isOpen: boolean;
  missingRequiredItemsForImport: MissingRequiredItemInfo;
  pendingImportData: PendingImportData;
};

const ReviewWorkflowImportModal = ({
  closeModal,
  onSuccessfulImport,
  isOpen,
  missingRequiredItemsForImport,
  pendingImportData,
}: ReviewWorkflowImportModalProps) => {
  const queryClient = useQueryClient();
  const addSnackBar = useSnackBar();
  const appDispatch = useAppDispatch();
  const workflowProps = useAppSelector(selectWorkflowProps);
  const workflowData = useAppSelector(selectWorkflowData);

  const { token: workflowToken } = workflowData;

  const [missingRequiredItems, setMissingRequiredItems] = useState([]);
  const [isCreatingMissingItems, setIsCreatingMissingItems] = useState<boolean>(false);

  useEffect(() => {
    const uniqueItems = uniqWith(missingRequiredItemsForImport,
      (issueA, issueB) => issueA.type === issueB.type
        && issueA.name === issueB.name);
    setMissingRequiredItems(uniqueItems);
  }, [missingRequiredItemsForImport]);

  const getStatusText = status => {
    switch (status) {
      case 'created':
        return 'Created';
      case 'created_pending_reasoncode_update':
        return 'Needs Update';
      case 'created_pending_outcome_update':
        return 'Needs Update';
      case 'archived':
        return 'Archived, Will Create New With (2) Suffix';
      case 'disabled':
        return 'Disabled, Contact Support';
      case 'unavailable':
        return 'Attribute Unavailable, Contact Support';
      default:
        return 'Missing';
    }
  };

  const getBeautifiedName = type => {
    switch (type) {
      case 'tag':
        return 'Tag';
      case 'outcome':
        return 'Outcome';
      case 'reasoncodegroup':
        return 'Reason Code Group';
      case 'attribute':
        return 'Service Attribute';
      case 'service':
        return 'Service';
      default:
        return '';
    }
  };

  const cols = React.useMemo<ColumnDef<{ type: string; name: string }>[]>(
    () => [
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'type',
        cell: ({ getValue }) => (
          getBeautifiedName(getValue())
        ),
      },
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name',
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: ({ getValue }) => (
          <Badge status={getValue() === 'created' ? 'success' : 'warning'}>
            {getStatusText(getValue())}
          </Badge>
        ),
      },
    ],
    []);

  const createMissingItems = async () => {
    setIsCreatingMissingItems(true);
    const updatedOutcomes = await createMissingOutcomes({
      missingRequiredItems,
    });
    const updatedOtherItems = await createOtherMissingItems({
      missingRequiredItems, workflowProps, updatedOutcomes,
    });

    return [...updatedOutcomes, ...updatedOtherItems];
  };

  const onCreateAllThenImport = async () => {
    const updatedIssues = await createMissingItems();
    setMissingRequiredItems(updatedIssues);
    setIsCreatingMissingItems(false);

    const filteredImportIssues = missingRequiredItems.filter(issue => !issue.status.includes('created') && issue.type !== 'service');
    if (filteredImportIssues.length > 0) {
      addSnackBar({
        title: 'Some items were not created successfully. You can try again or contact support.',
        type: 'danger',
      });
    } else {
      const disabledServices = missingRequiredItems.filter(issue => issue.type === 'service');
      if (disabledServices.length > 0) {
        addSnackBar({
          title: 'Some services are disabled in this organization. Please contact support to enable all of them.',
          type: 'danger',
        });
      } else {
        queryClient.invalidateQueries(useWorkflowProps.getQueryKey({ workflowToken }));
        const updatedThresholds = remapThresholdReasonCodeGroupIds({
          thresholds: pendingImportData.thresholds,
          missingRequiredItems,
        });

        if (pendingImportData.type === WorkflowJSONType.PARTIAL) {
          const {
            actions: actionsWithDeepMergedRules,
            actionsWithUpdatedRules,
          } = mergePendingRules({
            actions: { ...pendingImportData.actions, ...cloneDeep(workflowData.actions) },
            thresholds: { ...updatedThresholds, ...cloneDeep(workflowData.thresholds) },
            dataRoutes: {
              ...pendingImportData.dataRoutes,
              ...cloneDeep(workflowData.dataRoutes),
            },
            jsonData: pendingImportData.jsonData,
            pendingRules: pendingImportData.rulesPendingMerge,
            pendingExpressions: pendingImportData.expressionsPendingMerge,
            services: workflowProps.services,
            reasonCodeGroups: workflowProps.reasonCodeGroups,
          });

          const isContainingDataPassingNodes = checkExistenceOfDataPassingActions(
            actionsWithDeepMergedRules);
          appDispatch(importPartialWorkflowData({
            actions: actionsWithDeepMergedRules,
            thresholds: updatedThresholds,
            dataRoutes: pendingImportData.dataRoutes,
            validations: {
              actions: [
                ...pendingImportData.validations?.actions || [],
                ...actionsWithUpdatedRules,
              ],
              thresholds: [],
            },
          }));
          onSuccessfulImport(pendingImportData.type, isContainingDataPassingNodes);
        } else {
          const isContainingDataPassingNodes = checkExistenceOfDataPassingActions(
            pendingImportData.actions);
          appDispatch(importWorkflowData({
            actions: pendingImportData.actions,
            thresholds: updatedThresholds,
            dataRoutes: pendingImportData.dataRoutes,
            dependencies: pendingImportData.dependencies,
            workflowToken,
            validations: {
              actions: [
                ...pendingImportData.validations?.actions || [],
              ],
              thresholds: [],
            },
          }));
          onSuccessfulImport(pendingImportData.type, isContainingDataPassingNodes);
        }

        closeModal();
      }
    }
  };

  return (
    <Modal
      header="Review Workflow Import"
      id="resolve-import-issues-modal"
      isOpen={isOpen}
      onClose={closeModal}
      primaryAction={(
        <Button
          variant={ButtonVariant.primary}
          onClick={onCreateAllThenImport}
          loading={isCreatingMissingItems}
          disabled={isCreatingMissingItems}
        >
          {isCreatingMissingItems
            ? 'Creating...'
            : 'Create All and Import'}
        </Button>
      )}
      secondaryAction={(
        <Button
          variant={ButtonVariant.secondary}
          onClick={() => {
            closeModal();
          }}
        >
          Cancel
        </Button>
      )}
    >
      <Box direction="column" gap={5}>
        <Banner open variant="warning">
          <Text>
            Please review {pluralize('item', missingRequiredItems.length, true)} found not existing in this organization while importing the workflow.
            You can import the workflow after creating the missing tags and outcomes.
            For services, please contact support for enabling them.
          </Text>
        </Banner>
        <Table
          enableSorting
          columns={cols}
          data={missingRequiredItems}
          enableRowSelection={false}
        />
      </Box>
    </Modal>
  );
};

export default ReviewWorkflowImportModal;
